import React, { ReactNode, useState, useEffect } from 'react'
import { Button, Confirmation, Flex, Icon, Toaster } from '@ui'
import { Intent, Tabs, Tab } from '@blueprintjs/core'
import ImportProgress from './ImportProgress'
import SelectFileStep from './SelectFileStep'
import MappingStep from './MappingStep'
import PreviewStep from './PreviewStep'
import useActiveImport from './hooks/useActiveImport'
import useImportMappings from './hooks/useImportMappings'
import useFileReader from './hooks/useFileReader'
import api from '@api'
import ImportSummary from './ImportSummary'
import track from '@track'
import Event from '@track/Event'
import css from './importFlow.module.scss'

enum View {
  FileSelect,
  Mapping,
  Preview,
  Progress,
  Summary,
}

interface ImportFlowProps {
  dirty: boolean
  onClose: () => void
  reload?: () => void
  setDirty: (dirty: boolean) => void
}

export default function ImportFlow({ onClose, reload, setDirty, dirty }: ImportFlowProps) {
  const { mappings, mappingChecklist, mappingComplete, setMappings, initMappings } =
    useImportMappings()
  const { loading, activeImport } = useActiveImport()
  const { file, setFile, fileContent, parsedContent } = useFileReader(initMappings)
  const [view, setView] = useState<View>(View.FileSelect)
  const [importing, setImporting] = useState(false)
  const [importId, setImportId] = useState<string>()

  useEffect(() => {
    if (activeImport) {
      setImportId(activeImport.id)
      setView(View.Progress)
    }
  }, [activeImport])

  if (loading) {
    return <div style={{ height: 307 }} />
  }

  if (view === View.Progress && importId) {
    return (
      <ImportProgress
        {...{
          file,
          importId,
          callback: (error) => {
            if (error) {
              setView(View.Preview)
              Toaster.fail({ error })
              return
            }
            reload && reload()
            setView(View.Summary)
          },
        }}
      />
    )
  }

  if (view === View.Summary && importId) {
    return <ImportSummary id={importId} onClose={onClose} />
  }

  const tabs = [
    {
      id: View.FileSelect,
      title: (
        <Step index={1} active={view === View.FileSelect} complete={!!file}>
          Select file
        </Step>
      ),
      panel: (
        <div className={css.panel_container}>
          <div className={css.panel_container}>
            <SelectFileStep
              onChange={(file: File) => {
                setDirty(true)
                setFile(file)
                setView(View.Mapping)

                track.employeeImportDialog({
                  eventName: Event.IMPORT,
                })
              }}
            />
          </div>
          <StepperToolbar
            dirty={dirty}
            nextButton={
              <Button
                intent={Intent.PRIMARY}
                style={{ minWidth: 100 }}
                text="Next"
                rightIconName="long-arrow-right"
                disabled={!file}
                onClick={() => {
                  setView(View.Mapping)

                  track.employeeImportDialog({
                    eventName: Event.TAB_CLICK,
                    props: {
                      tabName: 'Mapping',
                    },
                  })
                }}
              />
            }
            onClose={onClose}
          />
        </div>
      ),
    },
    {
      id: View.Mapping,
      disabled: !file,
      title: (
        <Step complete={mappingComplete} active={view === View.Mapping} index={2}>
          Map fields
        </Step>
      ),
      panel: (
        <div className={css.panel_container}>
          <div className={css.panel_container}>
            <MappingStep {...{ mappingChecklist, mappings, setMappings }} />
          </div>
          <StepperToolbar
            dirty={dirty}
            backButton={
              <Button
                minimal
                text="Back"
                iconName="long-arrow-left"
                onClick={() => {
                  setView(View.FileSelect)

                  track.employeeImportDialog({
                    eventName: Event.TAB_CLICK,
                    props: {
                      tabName: 'FileSelect',
                    },
                  })
                }}
              />
            }
            nextButton={
              <Button
                intent={Intent.PRIMARY}
                style={{ minWidth: 100 }}
                text="Next"
                rightIconName="long-arrow-right"
                disabled={!mappingComplete}
                onClick={() => {
                  setView(View.Preview)

                  track.employeeImportDialog({
                    eventName: Event.TAB_CLICK,
                    props: {
                      tabName: 'Preview',
                    },
                  })
                }}
              />
            }
            onClose={onClose}
          />
        </div>
      ),
    },
    {
      id: View.Preview,
      disabled: !mappingComplete,
      title: (
        <Step index={3} active={view === View.Preview}>
          Preview
        </Step>
      ),
      panel: (
        <div className={css.panel_container}>
          <div className={css.panel_container}>
            <PreviewStep {...{ mappings, parsedContent }} />
          </div>
          <StepperToolbar
            dirty={dirty}
            backButton={
              <Button
                minimal
                text="Back"
                iconName="long-arrow-left"
                onClick={() => {
                  setView(View.Mapping)

                  track.employeeImportDialog({
                    eventName: Event.TAB_CLICK,
                    props: {
                      tabName: 'Mapping',
                    },
                  })
                }}
              />
            }
            nextButton={
              <Confirmation
                cancelable
                text="You are about to overwrite employee data, this action cannot be undone."
                confirmButtonText="Continue"
                cancelButtonText="Back"
                onConfirm={() => {
                  setImporting(true)

                  track.employeeImportDialog({
                    eventName: Event.TAB_CLICK,
                    props: {
                      tabName: 'Finish',
                    },
                  })

                  return api.employee.import
                    .run({
                      fileContent,
                      mappings,
                    })
                    .then((newImportId) => {
                      setDirty(false)
                      setImportId(newImportId)
                      setImporting(false)
                      setView(View.Progress)
                      reload && reload()
                    })
                    .catch((error) => {
                      setImporting(false)
                      Toaster.fail({ error })
                    })
                }}
              >
                <Button
                  intent={Intent.PRIMARY}
                  style={{ minWidth: 100 }}
                  text="Import"
                  disabled={importing}
                />
              </Confirmation>
            }
            onClose={onClose}
          />
        </div>
      ),
    },
    {
      id: 'n/a',
      disabled: true,
      title: (
        <Step active={false} index={4}>
          Import
        </Step>
      ),
      panel: <div>n/a</div>,
    },
  ]

  return (
    <Tabs
      id="import_stepper"
      selectedTabId={view}
      onChange={(tabId) => setView(tabId as View)}
      renderActiveTabPanelOnly
      vertical
      className="stepper"
    >
      {tabs.map((t) => (
        <Tab key={t.id} id={t.id} disabled={t.disabled} title={t.title} panel={t.panel} />
      ))}
    </Tabs>
  )
}

function StepperToolbar({
  dirty,
  backButton,
  nextButton,
  onClose,
}: {
  dirty: boolean
  backButton?: ReactNode
  nextButton: ReactNode
  onClose: () => void
}) {
  return (
    <div className={css.toolbar_container}>
      {backButton || <div />}
      <Flex>
        {dirty ? (
          <Confirmation text="Discard unsaved changes?" onConfirm={onClose}>
            <Button minimal text="Cancel" style={{ marginRight: 8 }} />
          </Confirmation>
        ) : (
          <Button minimal onClick={onClose} text="Cancel" style={{ marginRight: 8 }} />
        )}

        {nextButton}
      </Flex>
    </div>
  )
}

interface IStepProps {
  index: number
  active?: boolean
  complete?: boolean
  children: ReactNode
}

function Step({ index, active, complete, children }: IStepProps) {
  let indicator = <div className={css.step_indicator}>{index}</div>

  if (complete) {
    indicator = (
      <div className={css.step_indicator}>
        <Icon name="check" style={{ color: 'var(--primary-color)' }} />
      </div>
    )
  }

  if (active) {
    indicator = (
      <div
        className={css.step_indicator}
        style={{
          background: 'var(--primary-color)',
          color: 'white',
        }}
      >
        {index}
      </div>
    )
  }

  return (
    <Flex
      align="center"
      justify="space-between"
      style={{
        margin: '15px 0',
        fontWeight: active ? 500 : 400,
        width: '155px',
      }}
    >
      {children}
      {indicator}
    </Flex>
  )
}
